import type { Board, CouponIdentifiers, Outcome } from '../../../../schema/coupon/entry.js';
import type { EventNumber } from '../../../../schema/sport/entry.js';
import { createBrandedData } from '../../../../schema/type-utils/entry.js';
import type { IDefinitionService, IGetCouponUseCase, IUpdateCouponBoardUseCase } from '../../../domain/entry.js';

export type IResetCouponController = ReturnType<typeof resetCouponController>;

type BoardEventOutcomes = {
  group: number;
  event: EventNumber;
  outcomes: Outcome[];
};

const createBoardEventOutcomes = (eventCount: number, outcomeGroupCount: number): BoardEventOutcomes[] => {
  const boardEventOutcomes: BoardEventOutcomes[] = [];

  for (let eventNumber = 0; eventNumber < eventCount; eventNumber++) {
    for (let groupIndex = 0; groupIndex < outcomeGroupCount; groupIndex++) {
      boardEventOutcomes.push({
        event: createBrandedData<EventNumber>(eventNumber + 1),
        group: groupIndex,
        outcomes: [],
      });
    }
  }

  return boardEventOutcomes;
};

/**
 * Controller for creating a coupon.
 *
 * @param getCouponUseCase Use case for getting a coupon
 * @param updateCouponBoardUseCase Use case for updating a coupon
 * @param definitionService Definition service dependency
 * @returns Controller executor function
 */
export function resetCouponController(
  getCouponUseCase: IGetCouponUseCase,
  updateCouponBoardUseCase: IUpdateCouponBoardUseCase,
  definitionService: IDefinitionService
) {
  return async function controller({ couponId, couponIndex }: CouponIdentifiers): Promise<boolean> {
    const [getCoupon] = getCouponUseCase.execute({ couponId, couponIndex });

    const coupon = getCoupon();

    if (!coupon) {
      return false;
    }

    const definition = await definitionService.resolveDefinitionAsync(coupon.product);

    if (!definition) {
      return false;
    }

    const requests: Promise<number>[] = [];
    for (const [boardIndex, board] of Object.entries(coupon.boards)) {
      const boardEventOutcomes: BoardEventOutcomes[] = createBoardEventOutcomes(
        board.outcomes.length,
        definition.outcomeGroupCount
      );

      const payload = {
        outcomes: { values: boardEventOutcomes },
        ...({
          quickPickSize: undefined,
          quickPickVariant: undefined,
          reduction: [],
        } satisfies Partial<Board>),
      };

      const request = updateCouponBoardUseCase.execute({
        payload,
        couponId,
        couponIndex,
        boardIndex: Number(boardIndex),
      });

      requests.push(request);
    }

    await Promise.all(requests);

    return true;
  };
}
